import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Application from "../../Application";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import { IEvaluationType } from "../../model/evaluation/EvaluationType";
import GetEvaluationTypesInteractor from "../../interactor/evaluation/GetEvaluationTypesInteractor";
import CreateEvaluationTypeInteractor from "../../interactor/evaluation/CreateEvaluationTypeInteractor";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import UpdateEvaluationTypeInteractor from "../../interactor/evaluation/UpdateEvaluationTypeInteractor";
import { IEvaluationTypeRuleGroup } from "../../model/evaluation/EvaluationTypeRuleGroup";
import { IRuleGroup } from "../../model/evaluation/RuleGroup";
import GetEvaluationTypeRuleGroupsInteractor from "../../interactor/evaluation/GetEvaluationTypeRuleGroupsInteractor";
import JoinRuleGroupToEvaluationTypeInteractor from "../../interactor/evaluation/JoinRuleGroupToEvaluationTypeInteractor";
import ReleaseRuleGroupFromEvaluationTypeInteractor from "../../interactor/evaluation/ReleaseRuleGroupFromEvaluationTypeInteractor";

export interface TEvaluationTypesPresenter extends TLoadingAwarePresenter {
  evaluationTypes: IEvaluationType[] | undefined;
  evaluationTypeRuleGroups?: IEvaluationTypeRuleGroup[];
  ruleGroups?: IRuleGroupsPresentationModel[];
  assignedRuleGroupsIds?: number[];
  currentEvaluationType?: IEvaluationType;
}
export interface IEvaluationTypesPresenter
  extends TEvaluationTypesPresenter,
    TPresentable {
  getEvaluationTypes(): Promise<void>;
  setEvaluationTypes(evaluationTypes: IEvaluationType[]): void;
  createEvaluationType(evaluationType: IEvaluationType): void;
  updateEvaluationType(evaluationType: IEvaluationType): void;
  getEvaluationTypeRuleGroup(evaluationType: IEvaluationType): Promise<void>;
  setEvaluationTypeRuleGroups(
    evaluationTypeRuleGroups: IEvaluationTypeRuleGroup[]
  ): void;
  setRuleGroups(ruleGroups: IRuleGroup[]): void;
  onRuleGroupSelectChange(selectedRuleGroupsIds: number[]): void;
  onRowExpanded(expanded: any, record: any): void;
}

export interface IRuleGroupsPresentationModel extends IRuleGroup {
  label: string;
  value: number;
}
const defaultState: TEvaluationTypesPresenter = {
  evaluationTypes: []
};

const RuleGroupsPresentationModelMapper = (
  ruleGroups: IRuleGroup[]
): IRuleGroupsPresentationModel[] => {
  return ruleGroups.map((ruleGroup) => {
    return {
      ...ruleGroup,
      value: ruleGroup.id,
      label: ruleGroup.name
    };
  });
};
const AssignedRuleGroupsMapper = (
  evaluationTypeRuleGroups: IEvaluationTypeRuleGroup[]
): number[] => {
  return evaluationTypeRuleGroups.map((evaluationTypeRuleGroup) => {
    return evaluationTypeRuleGroup.businessRuleGroupId;
  });
};

const EvaluationTypesPresenter = withStore<
  IEvaluationTypesPresenter,
  TEvaluationTypesPresenter
>(({ application, store, loader, translate }): IEvaluationTypesPresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<TEvaluationTypesPresenter>();

  loader.start("evaluationTypesLoader");

  const setEvaluationTypes = (evaluationTypes: IEvaluationType[]) => {
    _store.update({ evaluationTypes });
    loader.stop("evaluationTypesLoader");
  };

  const getEvaluationTypes = async () => {
    try {
      let evaluationTypes = await _application.container
        .resolve<GetEvaluationTypesInteractor>("getEvaluationTypes")
        .execute();
      _store.update({ evaluationTypes });
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const createEvaluationType = async (evaluationType: IEvaluationType) => {
    try {
      await _application.container
        .resolve<CreateEvaluationTypeInteractor>("createEvaluationType")
        .execute(evaluationType);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(
          translate(
            "evaluationTypes_page.messages.create_evaluationType_success"
          )
        );
      await getEvaluationTypes();
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const updateEvaluationType = async (evaluationType: IEvaluationType) => {
    try {
      await _application.container
        .resolve<UpdateEvaluationTypeInteractor>("updateEvaluationType")
        .execute(evaluationType);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(
          translate(
            "evaluationTypes_page.messages.update_evaluationType_success"
          )
        );
      await getEvaluationTypes();
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };
  const setEvaluationTypeRuleGroups = (
    evaluationTypeRuleGroups: IEvaluationTypeRuleGroup[]
  ) => {
    _store.update({ evaluationTypeRuleGroups });
  };
  const setRuleGroups = (ruleGroups: IRuleGroup[]) => {
    ruleGroups = RuleGroupsPresentationModelMapper(ruleGroups);
    _store.update({ ruleGroups });
  };

  const getEvaluationTypeRuleGroup = async (
    evaluationType: IEvaluationType
  ) => {
    try {
      let evaluationTypeRuleGroups = await _application.container
        .resolve<GetEvaluationTypeRuleGroupsInteractor>(
          "getEvaluationTypeRuleGroups"
        )
        .execute(evaluationType);
      let assignedRuleGroupsIds = AssignedRuleGroupsMapper(
        evaluationTypeRuleGroups
      );
      _store.update({ assignedRuleGroupsIds });
      setEvaluationTypeRuleGroups(evaluationTypeRuleGroups);
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };
  const joinRuleGroupToEvaluationType = async (
    evaluationTypeId: number,
    ruleGroupId: number
  ) => {
    try {
      let { currentEvaluationType } = _store.getState();
      await _application.container
        .resolve<JoinRuleGroupToEvaluationTypeInteractor>(
          "joinRuleGroupToEvaluationType"
        )
        .execute(evaluationTypeId, ruleGroupId);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(
          translate("evaluationTypes_page.messages.joining_ruleGroup_success")
        );
      await getEvaluationTypeRuleGroup(currentEvaluationType);
      loader.stop("ruleGroupLoader");
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };
  const releaseRuleGroupFromEvaluationType = async (
    evaluationTypeRuleGroupId: number
  ) => {
    try {
      let { currentEvaluationType } = _store.getState();
      await _application.container
        .resolve<ReleaseRuleGroupFromEvaluationTypeInteractor>(
          "releaseRuleGroupFromEvaluationType"
        )
        .execute(evaluationTypeRuleGroupId);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(
          translate("evaluationTypes_page.messages.removing_ruleGroup_success")
        );
      await getEvaluationTypeRuleGroup(currentEvaluationType);
      loader.stop("ruleGroupLoader");
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const onRuleGroupSelectChange = async (selectedRuleGroupsIds: number[]) => {
    let {
      assignedRuleGroupsIds,
      currentEvaluationType,
      evaluationTypeRuleGroups
    } = _store.getState();
    loader.start("ruleGroupLoader");
    if (selectedRuleGroupsIds.length > assignedRuleGroupsIds.length) {
      let newRuleGroupIds = selectedRuleGroupsIds.filter(
        (selectedRuleGroupsId) =>
          !assignedRuleGroupsIds.includes(selectedRuleGroupsId)
      );
      newRuleGroupIds.forEach(
        async (ruleGroupId) =>
          await joinRuleGroupToEvaluationType(
            currentEvaluationType.id,
            ruleGroupId
          )
      );
    } else if (selectedRuleGroupsIds.length < assignedRuleGroupsIds.length) {
      let removedRuleGroupCodes = assignedRuleGroupsIds.filter(
        (assignedRuleGroupId) =>
          !selectedRuleGroupsIds.includes(assignedRuleGroupId)
      );
      removedRuleGroupCodes.forEach(async (ruleGroupCode) => {
        let evaluationTypeRuleGroup: IEvaluationTypeRuleGroup =
          evaluationTypeRuleGroups.find(
            (item) => item.businessRuleGroupId === ruleGroupCode
          );
        await releaseRuleGroupFromEvaluationType(evaluationTypeRuleGroup.id);
      });
    }
    _store.update({ assignedRuleGroupsIds });
  };

  const setExpandedRowData = async (evaluationType: IEvaluationType) => {
    loader.start("ruleGroupLoader");
    _store.update({ currentEvaluationType: evaluationType });
    await getEvaluationTypeRuleGroup(evaluationType);
    loader.stop("ruleGroupLoader");
  };
  const onRowExpanded = async (expanded: any, record: any) => {
    if (expanded) {
      await setExpandedRowData(record);
    }
  };

  return {
    ...state,

    store: _store,
    loader,
    application: _application,
    translate,
    getEvaluationTypes,
    setEvaluationTypes,
    createEvaluationType,
    updateEvaluationType,
    setEvaluationTypeRuleGroups,
    getEvaluationTypeRuleGroup,
    onRowExpanded,
    onRuleGroupSelectChange,
    setRuleGroups
  };
}, defaultState);

export default EvaluationTypesPresenter;
